import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import e2iApi from '../../services/e2iApi';
import type { UserOption } from '../../types/e2i';
import { errorText } from '../../utils/e2i';

interface Props {
  id: string;
  companyId: number;
  label: string;
  value: UserOption | null;
  onChange: (value: UserOption | null) => void;
  disabled?: boolean;
  helperText?: React.ReactNode;
}

/** A PRPO user who can open the company, searched by name. */
const UserPicker: React.FC<Props> = ({ id, companyId, label, value, onChange, disabled, helperText }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ticket = useRef(0);

  useEffect(() => {
    if (!open || !companyId) return undefined;
    const q = input.trim();
    if (value && q === value.name) return undefined;
    const mine = ++ticket.current;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      e2iApi.users(companyId, q)
        .then((rows) => { if (mine === ticket.current) setOptions(rows); })
        .catch((e: any) => { if (mine === ticket.current) setError(errorText(e, 'The users could not be loaded')); })
        .finally(() => { if (mine === ticket.current) setLoading(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [open, input, companyId, value]);

  return (
    <Autocomplete
      id={id}
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      options={value && !options.some((o) => o.userId === value.userId) ? [value, ...options] : options}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a.userId === b.userId}
      getOptionLabel={(o) => o.name}
      onChange={(_e, v) => onChange(v)}
      onInputChange={(_e, v) => setInput(v)}
      loading={loading}
      noOptionsText="No user found"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error || helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default UserPicker;
